import { Coordinates } from "@hermes/api-model-common";
import { Shop } from "@hermes/api-model-shop";
import { MapBounds } from "@hermes/map-utils";
import { FindShopsParameters } from "@hermes/shop-utils";

/** State interface for shop */
export interface ShopLocatorState {
    shopList: ShopListState;
    selectedLocation: SelectedLocationState;
    hoveredShop: HoveredShopState;
    items: ItemsState;
    config: ConfigState;
}

export enum ShopsStateStatus {
    Uninitialized = "uninitialized",
    Loading = "loading",
    Loaded = "loaded",
    Error = "error",
}

export type ShopsStateData = Record<string, Shop>;

export interface ShopListState {
    status: ShopsStateStatus;
    error: string | undefined;
    shops: ShopsStateData;
    parameters: FindShopsParameters;
}

export interface SelectedLocationCoordinates {
    address: string;
    coordinates: Coordinates;
    bounds?: MapBounds;
    isCountry: boolean;
}

export interface SelectedLocationCountry {
    countryCode: string;
    isCountry: true;
}

export interface SelectedLocationCity {
    countryCode: string;
    city: string;
    isCountry: false;
}

export const NO_SELECTED_LOCATION = "NO_SELECTED_LOCATION" as const;

export type SelectedLocationState =
    | SelectedLocationCoordinates
    | SelectedLocationCity
    | SelectedLocationCountry
    | typeof NO_SELECTED_LOCATION;

export enum HoveredShopEventOrigin {
    List = "list",
    Map = "map",
}

export interface HoveredShop {
    shopId: string;
    coordinates: Coordinates;
    origin: HoveredShopEventOrigin;
}

export const NO_HOVERED_SHOP = "NO_HOVERED_SHOP" as const;
export type HoveredShopState = HoveredShop | typeof NO_HOVERED_SHOP;

export type ShopLocatorExtraInfos = Record<
    string,
    {
        meters: number;
        directionUrl: string;
    }
>;

export interface ItemsState {
    shopIdsToIgnore: string[];
    visibleShopIds: string[];
    extraInfos: ShopLocatorExtraInfos;
}

export enum ShopLocatorMode {
    Map = "map",
    NoMap = "no-map",
    MapMobile = "map-mobile",
}

export interface ConfigState {
    mode: ShopLocatorMode;
}

export const initialShopLocatorState: ShopLocatorState = {
    shopList: {
        shops: {},
        status: ShopsStateStatus.Uninitialized,
        error: undefined,
        parameters: {},
    },
    items: {
        shopIdsToIgnore: [],
        visibleShopIds: [],
        extraInfos: {},
    },
    hoveredShop: "NO_HOVERED_SHOP",
    selectedLocation: "NO_SELECTED_LOCATION",
    config: { mode: ShopLocatorMode.NoMap },
};
